import { User, Crown } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import type { Participant, Session } from '../lib/types';

interface ParticipantListProps {
  participants: Participant[];
  currentParticipant: Participant;
  session: Session;
}

export function ParticipantList({ participants, currentParticipant, session }: ParticipantListProps) {
  const { t } = useTranslation();

  // Sort participants to show creator first, then alphabetically
  const sortedParticipants = [...participants].sort((a, b) => {
    // Creator always comes first
    if (a.nickname === session.creator_nickname) return -1;
    if (b.nickname === session.creator_nickname) return 1;
    // Then sort alphabetically by nickname
    return a.nickname.localeCompare(b.nickname);
  });

  return (
    <div className="rounded-lg bg-white p-4 shadow-sm">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold text-gray-900">{t('session.participants')}</h2>
        <span className="text-sm text-gray-600">
          {participants.length}/{session.max_participants}
        </span>
      </div>
      <div className="space-y-2">
        {sortedParticipants.map((participant) => (
          <div
            key={participant.id}
            className={`flex items-center gap-2 rounded-lg p-2 ${
              participant.id === currentParticipant.id
                ? 'bg-primary-50 text-primary-700'
                : 'bg-gray-50 text-gray-700'
            }`}
          >
            {participant.nickname === session.creator_nickname ? (
              <Crown className="h-5 w-5 text-amber-500 flex-shrink-0" />
            ) : (
              <User className="h-5 w-5 flex-shrink-0" />
            )}
            <span className="flex-1 truncate font-medium">
              {participant.nickname}
            </span>
            {participant.id === currentParticipant?.id && (
              <span className="text-sm text-primary-600 flex-shrink-0">({t('letter_grid.entry.you')})</span>
            )}
          </div>
        ))}
      </div>
      {session.is_finished && (
        <div className="mt-4 rounded-lg border border-dashed border-gray-300 p-3 text-center text-sm text-gray-500">
          {t('session.finished')}
        </div>
      )}
    </div>
  );
}